/* ─────────────────────────────────────────────────────────────────────────────
   GrowthOS Executive — page-local ports of the reference app's presentation
   helpers (components/KpiCard.tsx, TaperedFunnel.tsx
   and the .tile-3d / .kpi-card / .badge-window / .section-title CSS), so the
   Executive page renders the same numbers, labels and shapes as the original.
   ──────────────────────────────────────────────────────────────────────────── */
import React from 'react'
import { Box, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import DateRangeIcon from '@mui/icons-material/DateRange'
import { GX_BORDER, GX_INK_500, GX_INK_900 } from '../growthos-shared/GrowthOsUi'
import { currency, integer, percentage } from '../growthos-shared/gxFormat'

export const INK_400 = '#94a3b8'
export const ACCENT = '#4f46e5'
export const ACCENT_GRAD = 'linear-gradient(90deg, #4f46e5, #06b6d4)'
export const DONUT_COLORS = ['#4F46E5', '#06B6D4', '#0D9488', '#D97706', '#E11D48', '#7C3AED']
export const CARD_SHADOW = '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06)'
export const CARD_SHADOW_HOVER = '0 2px 4px rgba(15,23,42,0.06), 0 16px 40px rgba(15,23,42,0.10)'

export function ChartTooltip({ active, payload, label, moneyKeys = [] }) {
  if (!active || !payload?.length) return null
  return (
    <Box sx={{ borderRadius: '12px', border: `1px solid ${GX_BORDER}`, bgcolor: '#fff', p: 1.5, fontSize: 12, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}>
      <Box sx={{ mb: 1, fontWeight: 600 }}>{label}</Box>
      {payload.map((item) => (
        <Box key={item.dataKey} sx={{ display: 'flex', minWidth: 160, justifyContent: 'space-between', gap: 2.5, py: 0.25 }}>
          <span style={{ color: item.color }}>{item.name}</span>
          <span style={{ fontWeight: 600 }}>{moneyKeys.includes(item.dataKey) ? currency(item.value) : integer(item.value)}</span>
        </Box>
      ))}
    </Box>
  )
}

// ── .badge-window ScopeTag ───────────────────────────────────────────────────
export function ScopeTag({ label, title, size = 12 }) {
  return (
    <Box component="span" title={title} sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.75, borderRadius: '9999px', px: 1.5, py: 0.5,
      fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', color: ACCENT,
      border: '1px solid rgba(79,70,229,0.25)', background: 'linear-gradient(90deg, rgba(79,70,229,0.08), rgba(6,182,212,0.08))',
    }}>
      <DateRangeIcon sx={{ fontSize: size }} /> {label}
    </Box>
  )
}

// ── .section-title (uppercase, tracked) with a leading icon ──────────────────
export function SectionTitle({ icon, iconColor = ACCENT, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em', color: GX_INK_500 }}>
      {icon && <Box sx={{ display: 'flex', color: iconColor, '& svg': { fontSize: 16 } }}>{icon}</Box>}
      {children}
    </Box>
  )
}

// ── .card ────────────────────────────────────────────────────────────────────
export const cardSx = {
  borderRadius: '16px', border: `1px solid ${GX_BORDER}`, boxShadow: CARD_SHADOW,
  background: 'linear-gradient(160deg, #ffffff 0%, #fbfbfe 100%)',
  transition: 'box-shadow 280ms ease, border-color 280ms ease',
  '&:hover': { boxShadow: CARD_SHADOW_HOVER, borderColor: 'rgba(79,70,229,0.16)' },
}

// ── KpiCard (accent bar + info tooltip + tinted icon chip) ───────────────────
const ACCENTS = {
  indigo: 'linear-gradient(90deg,#4F46E5,#06B6D4)',
  teal: 'linear-gradient(90deg,#0D9488,#10B981)',
  amber: 'linear-gradient(90deg,#D97706,#F59E0B)',
  rose: 'linear-gradient(90deg,#E11D48,#F472B6)',
}
const ICON_TINT = {
  indigo: { bg: 'rgba(79,70,229,0.10)', fg: '#4F46E5' },
  teal: { bg: 'rgba(13,148,136,0.10)', fg: '#0D9488' },
  amber: { bg: 'rgba(217,119,6,0.10)', fg: '#D97706' },
  rose: { bg: 'rgba(225,29,72,0.10)', fg: '#E11D48' },
}

export function KpiCard({ label, value, sub, accent = 'indigo', icon, definition, loading = false, onClick, active }) {
  const tint = ICON_TINT[accent] || ICON_TINT.indigo
  return (
    <Box
      component={onClick ? 'button' : 'div'} type={onClick ? 'button' : undefined} onClick={onClick}
      aria-expanded={onClick ? Boolean(active) : undefined}
      sx={{
        ...cardSx, position: 'relative', overflow: 'hidden', p: 2.5, textAlign: 'left', width: '100%',
        font: 'inherit', cursor: onClick ? 'pointer' : 'default', display: 'block',
        transition: 'box-shadow 280ms ease, border-color 280ms ease, transform 220ms ease',
        '&:hover': { boxShadow: CARD_SHADOW_HOVER, borderColor: 'rgba(79,70,229,0.16)', transform: 'translateY(-3px)' },
        '&::before': { content: '""', position: 'absolute', inset: '0 0 auto 0', height: 3, background: ACCENTS[accent] || ACCENT_GRAD },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography component="span" sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: GX_INK_500 }}>{label}</Typography>
          {definition && (
            <Tooltip title={definition} arrow>
              <InfoOutlinedIcon sx={{ fontSize: 13, color: '#cbd5e1', cursor: 'help', '&:hover': { color: ACCENT } }} />
            </Tooltip>
          )}
        </Box>
        {icon && (
          <Box sx={{ width: 32, height: 32, flexShrink: 0, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: tint.bg, color: tint.fg, '& svg': { fontSize: 16 } }}>
            {icon}
          </Box>
        )}
      </Box>
      {loading
        ? <Box sx={{ mt: 1.5, height: 32, width: 96, borderRadius: '8px', background: 'linear-gradient(90deg, #eef0f6, #f6f7fb, #eef0f6)' }} />
        : <Typography sx={{ mt: 1, fontSize: '1.9rem', fontWeight: 700, lineHeight: 1, letterSpacing: '-0.025em', color: GX_INK_900, fontVariantNumeric: 'tabular-nums' }}>{value ?? '—'}</Typography>}
      {sub && !loading && <Typography sx={{ mt: 1, fontSize: 12, color: GX_INK_500 }}>{sub}</Typography>}
    </Box>
  )
}

// ── .tile-3d (risk / cohort tiles) ───────────────────────────────────────────
export const tile3dSx = {
  position: 'relative', borderRadius: '12px', border: `1px solid ${GX_BORDER}`, p: 2, textAlign: 'left', font: 'inherit', width: '100%',
  background: 'linear-gradient(160deg, #ffffff 0%, #f8fafc 100%)', cursor: 'pointer',
  boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset, 0 6px 14px -9px rgba(15,23,42,0.16), 0 2px 4px rgba(15,23,42,0.04)',
  transition: 'transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease',
  '&:hover': {
    transform: 'translateY(-3px)', borderColor: 'rgba(79,70,229,0.28)',
    boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset, 0 16px 26px -12px rgba(79,70,229,0.3), 0 4px 10px rgba(15,23,42,0.08)',
  },
}

// ── TaperedFunnel (sqrt-scaled centered trapezoids) ──────────────────────────
export function funnelWidthPct(pct, floor = 24) {
  const p = Math.max(0, Math.min(100, pct ?? 0)) / 100
  return floor + (100 - floor) * Math.sqrt(p)
}

export function TaperedFunnel({ stages, minWidthPct }) {
  return (
    <Box sx={{ mx: 'auto', display: 'flex', maxWidth: 672, flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
      {stages.map((stage) => (
        <Box key={stage.key} sx={{ width: `${funnelWidthPct(stage.pct, minWidthPct)}%` }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 2, color: '#fff',
            background: stage.gradient, clipPath: 'polygon(4% 0%, 96% 0%, 100% 100%, 0% 100%)',
            boxShadow: `0 20px 32px -14px ${stage.shadow}, 0 8px 14px -8px ${stage.shadow}, inset 0 2px 0 rgba(255,255,255,0.4), inset 0 -4px 8px rgba(0,0,0,0.22)`,
            textShadow: '0 1px 2px rgba(0,0,0,0.25)',
          }}>
            <Box sx={{ display: 'flex', flexShrink: 0, opacity: 0.9, '& svg': { fontSize: 17 } }}>{stage.icon}</Box>
            <Box sx={{ display: 'flex', minWidth: 0, flex: 1, alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5 }}>
              <Typography component="span" noWrap sx={{ fontSize: 14, fontWeight: 600 }}>{stage.label}</Typography>
              <Typography component="span" sx={{ flexShrink: 0, fontSize: 16, fontWeight: 700 }}>
                {integer(stage.count)}
                {stage.pct != null && <Box component="span" sx={{ ml: 0.5, fontSize: 12, fontWeight: 500, opacity: 0.8 }}>({percentage(stage.pct)})</Box>}
              </Typography>
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  )
}
